/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import size from 'lodash/size';
import { Spinner, SecurityHelper } from 'sarsaparilla';
import * as actions from '../../actions/availability';
import { readRuleAsMoment } from '../../utils/utilsNew';
import { PERMIT, ruleProps } from '../../props/permit';
import PreferredDatesList from '../../components/multi-division/PreferredDatesList';

const propTypes = {
    permit: PERMIT.isRequired,
    aggSeasonRules: PropTypes.arrayOf(ruleProps).isRequired,
    doGetAvailability: PropTypes.func.isRequired,
    validation: PropTypes.shape({}),
    availabilities: PropTypes.shape({}),
    isLottery: PropTypes.bool,
};

const defaultProps = {
    isLottery: false,
};

export function getAvailabilityKey(permitId, divisionId) {
    return `${permitId}-${divisionId}`;
}

export function AvailabilityContainer({
    permit,
    availabilities,
    aggSeasonRules,
    doGetAvailability,
    ...rest
}) {
    const [display, setDisplay] = useState(false);

    useEffect(() => {
        Object.keys(permit.divisions).map(doGetAvailability);
    }, [JSON.stringify(aggSeasonRules)]);

    useEffect(() => {
        const errors = Object.keys(permit.divisions).reduce((error, divisionId) => {
            const divisionAvailability = get(
                availabilities,
                `${getAvailabilityKey(permit.id, divisionId)}`
            );
            const availabilityError = get(divisionAvailability, 'error');

            if (!divisionAvailability) {
                return error.concat('not loaded yet');
            }

            if (availabilityError) {
                return error.concat(availabilityError);
            }

            return error;
        }, []);

        setDisplay(size(errors) === 0);
    }, [availabilities, permit.divisions, permit.id]);

    return display ? (
        <PreferredDatesList permit={permit} {...rest} />
    ) : (
        <div className="per-preferred-dates-list">
            <Spinner />
        </div>
    );
}

function getAvailability(permit, divisionId, aggSeasonRules, dispatch, isLottery) {
    const startDate = readRuleAsMoment(
        isLottery ? 'SeasonStart' : 'SeasonStart',
        aggSeasonRules,
        permit.time_zone,
        divisionId,
        true
    );
    const endDate = readRuleAsMoment(
        isLottery ? 'SeasonEnd' : 'SeasonEnd',
        aggSeasonRules,
        permit.time_zone,
        divisionId,
        true
    );
    if (!startDate.isValid() || !endDate.isValid) return;

    dispatch(
        actions.fetchAvailability(
            permit.id,
            divisionId,
            startDate,
            endDate,
            SecurityHelper.isCommercialAccount(),
            isLottery,
            getAvailabilityKey(permit.id, divisionId)
        )
    );
}

AvailabilityContainer.propTypes = propTypes;
AvailabilityContainer.defaultProps = defaultProps;

function mapStateToProps(state) {
    return {
        availabilities: state.permit.availabilities,
    };
}

function mapDispatchToProps(dispatch, ownProps) {
    return {
        doGetAvailability: (divisionId) =>
            getAvailability(
                ownProps.permit,
                divisionId,
                ownProps.aggSeasonRules,
                dispatch,
                ownProps.isLottery
            ),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(AvailabilityContainer);
